import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Button,
  Divider
} from '@mui/material';
import {
  PersonAdd as FollowIcon,
  Chat as ChatIcon,
  Visibility as ViewIcon,
  Favorite as LikeIcon,
  CheckCircle as MatchIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Pending as PendingIcon,
  CheckCircle as AcceptedIcon,
  Cancel as RejectedIcon,
  Phone as CallIcon,
  Videocam as VideoIcon
} from '@mui/icons-material';
import { verificationAPI } from '../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const InteractionsDashboard = () => {
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchInteractions();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchInteractions();
  }, [page, typeFilter, statusFilter, search]);

  const fetchInteractions = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 20,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined
      };
      const response = await verificationAPI.getInteractions(params);
      setInteractions(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      toast.error('Failed to fetch interactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await verificationAPI.getInteractionStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const types = ['all', 'follows', 'chats', 'views', 'likes', 'matches'];
    setTypeFilter(types[newValue]);
    setPage(1);
  };

  const getStatusChip = (status) => {
    const statusMap = {
      pending: { color: 'warning', label: 'Pending' },
      accepted: { color: 'success', label: 'Accepted' },
      rejected: { color: 'error', label: 'Rejected' }
    };
    const info = statusMap[status?.toLowerCase()] || { color: 'default', label: status || 'Unknown' };
    return <Chip size="small" color={info.color} label={info.label} />;
  };

  const getInteractionType = (interaction) => {
    // Check for interaction_type_display first
    if (interaction.interaction_type_display) {
      return interaction.interaction_type_display;
    }
    // Check for interaction_type
    if (interaction.interaction_type) {
      if (interaction.interaction_type === 'view_profile') return 'view';
      if (interaction.interaction_type === 'send_request') return 'request';
      if (interaction.interaction_type === 'like') return 'like';
      if (interaction.interaction_type === 'swipe') return 'swipe';
      return interaction.interaction_type;
    }
    // Check for type field (from notifications)
    if (interaction.type) {
      if (['chat', 'audio_call', 'video_call'].includes(interaction.type)) {
        return 'chat';
      }
      return interaction.type;
    }
    // Default to 'follow' if status exists
    if (interaction.status) return 'follow';
    return 'unknown';
  };

  const getTypeIcon = (type) => {
    const typeMap = {
      follow: <FollowIcon fontSize="small" />,
      follows: <FollowIcon fontSize="small" />,
      chat: <ChatIcon fontSize="small" />,
      chats: <ChatIcon fontSize="small" />,
      view: <ViewIcon fontSize="small" />,
      views: <ViewIcon fontSize="small" />,
      like: <LikeIcon fontSize="small" />,
      likes: <LikeIcon fontSize="small" />,
      match: <MatchIcon fontSize="small" />,
      matches: <MatchIcon fontSize="small" />,
      request: <PendingIcon fontSize="small" />,
      swipe: <ViewIcon fontSize="small" />,
      audio_call: <CallIcon fontSize="small" />,
      video_call: <VideoIcon fontSize="small" />
    };
    return typeMap[type] || <ViewIcon fontSize="small" />;
  };

  const getTypeLabel = (type) => {
    const labelMap = {
      follow: 'Follow',
      follows: 'Follow',
      chat: 'Chat',
      chats: 'Chat',
      view: 'Profile View',
      views: 'Profile View',
      like: 'Like',
      likes: 'Like',
      match: 'Match',
      matches: 'Match',
      request: 'Request',
      swipe: 'Swipe',
      audio_call: 'Audio Call',
      video_call: 'Video Call'
    };
    return labelMap[type] || type;
  };

  const renderInteractionCard = (interaction) => {
    const type = getInteractionType(interaction);
    
    let content;
    switch (type) {
      case 'follow':
      case 'follows':
        content = (
          <Box>
            <Typography variant="body2">
              <strong>{interaction.follower_first_name || interaction.viewer_first_name || 'Unknown'} {interaction.follower_last_name || interaction.viewer_last_name || ''}</strong>
              {' → '}
              <strong>{interaction.following_first_name || interaction.target_first_name || 'Unknown'} {interaction.following_last_name || interaction.target_last_name || ''}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Phone: {interaction.follower_phone || interaction.viewer_phone || 'N/A'} → {interaction.following_phone || interaction.target_phone || 'N/A'}
            </Typography>
            {interaction.status && (
              <Box mt={1}>
                {getStatusChip(interaction.status)}
              </Box>
            )}
          </Box>
        );
        break;
      
      case 'chat':
      case 'chats':
        content = (
          <Box>
            <Typography variant="body2">
              <strong>{interaction.sender_first_name || 'Unknown'} {interaction.sender_last_name || ''}</strong>
              {' → '}
              <strong>{interaction.user_first_name || 'Unknown'} {interaction.user_last_name || ''}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              {interaction.type === 'audio_call' ? '📞 Audio Call' : 
               interaction.type === 'video_call' ? '📹 Video Call' : 
               '💬 Message'}
            </Typography>
            {interaction.message && (
              <Typography variant="body2" sx={{ mt: 1, bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                {interaction.message}
              </Typography>
            )}
          </Box>
        );
        break;
      
      case 'view':
      case 'views':
        content = (
          <Box>
            <Typography variant="body2">
              <strong>{interaction.viewer_first_name || interaction.user_first_name || 'Unknown'} {interaction.viewer_last_name || interaction.user_last_name || ''}</strong>
              {' viewed '}
              <strong>{interaction.target_first_name || 'Unknown'} {interaction.target_last_name || ''}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Phone: {interaction.viewer_phone || interaction.user_phone || 'N/A'} → {interaction.target_phone || 'N/A'}
            </Typography>
          </Box>
        );
        break;
      
      case 'like':
      case 'likes':
        content = (
          <Box>
            <Typography variant="body2">
              <strong>{interaction.liker_first_name || interaction.user_first_name || 'Unknown'} {interaction.liker_last_name || interaction.user_last_name || ''}</strong>
              {' liked '}
              <strong>{interaction.target_first_name || 'Unknown'} {interaction.target_last_name || ''}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Phone: {interaction.liker_phone || interaction.user_phone || 'N/A'} → {interaction.target_phone || 'N/A'}
            </Typography>
          </Box>
        );
        break;
      
      case 'match':
      case 'matches':
        content = (
          <Box>
            <Typography variant="body2">
              <strong>{interaction.user_first_name || 'Unknown'} {interaction.user_last_name || ''}</strong>
              {' matched with '}
              <strong>{interaction.matched_first_name || 'Unknown'} {interaction.matched_last_name || ''}</strong>
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Score: {interaction.match_score || 0}% | Phone: {interaction.user_phone || 'N/A'} ↔ {interaction.matched_phone || 'N/A'}
            </Typography>
            {interaction.status && (
              <Box mt={1}>
                {getStatusChip(interaction.status)}
              </Box>
            )}
          </Box>
        );
        break;
      
      default:
        content = (
          <Box>
            <Typography variant="body2" color="textSecondary">
              Unknown interaction type
            </Typography>
            <Typography variant="caption" display="block" color="textSecondary">
              {JSON.stringify(interaction).substring(0, 100)}...
            </Typography>
          </Box>
        );
    }

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              {content}
            </Box>
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1} minWidth={120}>
              <Chip 
                size="small" 
                icon={getTypeIcon(type)}
                label={getTypeLabel(type)}
                variant="outlined"
              />
              <Typography variant="caption" color="textSecondary">
                {moment(interaction.created_at).fromNow()}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Stats Cards
  const renderStats = () => {
    if (!stats) return null;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
            <Typography variant="h4">{stats.follows?.total || 0}</Typography>
            <Typography color="textSecondary">Total Follows</Typography>
            <Box display="flex" justifyContent="center" gap={1} mt={1}>
              <Chip size="small" label={`Pending: ${stats.follows?.pending || 0}`} color="warning" />
              <Chip size="small" label={`Accepted: ${stats.follows?.accepted || 0}`} color="success" />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
            <Typography variant="h4">{stats.chats?.total || 0}</Typography>
            <Typography color="textSecondary">Total Chats/Calls</Typography>
            <Box display="flex" justifyContent="center" gap={1} mt={1}>
              <Chip size="small" label={`Messages: ${stats.chats?.messages || 0}`} color="primary" />
              <Chip size="small" label={`Calls: ${(stats.chats?.audio_calls || 0) + (stats.chats?.video_calls || 0)}`} color="info" />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
            <Typography variant="h4">{stats.profileViews?.total || 0}</Typography>
            <Typography color="textSecondary">Profile Views</Typography>
            <Typography variant="caption" display="block" color="textSecondary">
              {stats.profileViews?.unique_viewers || 0} unique viewers
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fce4ec' }}>
            <Typography variant="h4">{stats.likes?.total || 0}</Typography>
            <Typography color="textSecondary">Total Likes</Typography>
            <Typography variant="caption" display="block" color="textSecondary">
              {stats.likes?.unique_likers || 0} unique users
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
            <Typography variant="h4">{stats.matches?.total || 0}</Typography>
            <Typography color="textSecondary">Matches</Typography>
            <Typography variant="caption" display="block" color="textSecondary">
              Avg Score: {stats.matches?.avg_score || 0}%
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e0f7fa' }}>
            <Typography variant="h4">
              {stats.recentActivity?.reduce((sum, item) => sum + (item.count || 0), 0) || 0}
            </Typography>
            <Typography color="textSecondary">Last 24 Hours</Typography>
            <Box display="flex" justifyContent="center" gap={0.5} flexWrap="wrap">
              {stats.recentActivity?.map((item) => (
                <Chip 
                  key={item.type}
                  size="small" 
                  label={`${item.type}: ${item.count}`} 
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Top Users
  const renderTopUsers = () => {
    if (!stats?.topUsers?.length) return null;

    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Top Active Users
        </Typography>
        <Grid container spacing={2}>
          {stats.topUsers.map((user, index) => (
            <Grid item xs={12} sm={6} md={4} key={user.id}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {index + 1}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {user.first_name || 'Unknown'} {user.last_name || ''}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {user.phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                    <Chip size="small" label={`Views: ${user.profile_views || 0}`} />
                    <Chip size="small" label={`Likes: ${user.likes || 0}`} />
                    <Chip size="small" label={`Requests: ${user.requests_sent || 0}`} />
                    <Chip size="small" label={`Total: ${user.interaction_count || 0}`} color="primary" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  if (loading && !interactions.length) {
    return (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          User Interactions
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchInteractions(); fetchStats(); }}
        >
          Refresh
        </Button>
      </Box>

      {renderStats()}
      {renderTopUsers()}

      {/* Filters and Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab label="All" />
          <Tab label="Follows" />
          <Tab label="Chats" />
          <Tab label="Views" />
          <Tab label="Likes" />
          <Tab label="Matches" />
        </Tabs>

        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {tabValue !== 0 && tabValue !== 2 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          )}
          <TextField
            size="small"
            placeholder="Search users..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Box>
      </Paper>

      {/* Interactions List */}
      {interactions.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary">
            No interactions found
          </Typography>
        </Paper>
      ) : (
        <Box>
          {interactions.map((interaction) => (
            <div key={interaction.id || Math.random()}>
              {renderInteractionCard(interaction)}
            </div>
          ))}
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default InteractionsDashboard;